"use client";

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "xgodo:hidden-jobs";

/** Read the persisted set, tolerating absent/corrupt storage. */
function read(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((v) => typeof v === "string")
      : [];
  } catch {
    // Private-mode / quota / hand-edited value — treat as "nothing hidden"
    // rather than taking the page down over a cosmetic preference.
    return [];
  }
}

/**
 * The set of job ids the user has hidden from `/jobs`, persisted in
 * localStorage.
 *
 * The legacy control this replaces was inert: it ran
 * `localStorage.setItem("hidejob", JSON.stringify({ job }))` and nothing in the
 * codebase ever read that key back, so clicking the eye hid nothing and each
 * click overwrote the last. This stores a LIST of ids under a namespaced key
 * and actually filters the list, which is what the icon has always implied.
 *
 * Client-only and per-browser: there is no hide/unhide endpoint on the backend,
 * so this deliberately does not pretend to sync across devices.
 */
export function useHiddenJobs() {
  // Start empty and hydrate in an effect: reading localStorage during render
  // makes the server and first client paint disagree.
  const [hidden, setHidden] = useState<string[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setHidden(read());
    setReady(true);
  }, []);

  const persist = useCallback((next: string[]) => {
    setHidden(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Storage unavailable — the hide still applies for this session.
    }
  }, []);

  const hide = useCallback(
    (jobId: string) =>
      persist(read().includes(jobId) ? read() : [...read(), jobId]),
    [persist],
  );

  const showAll = useCallback(() => persist([]), [persist]);

  return {
    /** True once localStorage has been read; until then nothing is filtered. */
    ready,
    hiddenIds: hidden,
    hiddenCount: hidden.length,
    isHidden: useCallback((jobId: string) => hidden.includes(jobId), [hidden]),
    hide,
    showAll,
  };
}
